import { motion } from "framer-motion";
import { FaApple, FaGooglePlay } from "react-icons/fa";
import { ArrowDown, MapPin } from "lucide-react";

const VIDEO = "https://customer-assets.emergentagent.com/job_9c6a7b9b-6c36-4fd3-a6ee-0e90154b575f/artifacts/1gkcfjre_2627bbed9d6c068e50d2aadcca11ddbb1743095925.mp4";

export default function Hero() {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden text-white grain"
    >
      {/* Background video */}
      <video
        data-testid="hero-bg-video"
        className="absolute inset-0 h-full w-full object-cover"
        src={VIDEO}
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
      />
      {/* Overlays */}
      <div className="absolute inset-0 bg-black/60" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-36 md:pt-44 pb-24 md:pb-32 flex flex-col min-h-[100svh] justify-between">
        <div className="max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="inline-flex items-center gap-2 px-3 py-1 border border-white/15 rounded-full text-xs uppercase tracking-[0.25em] text-lime-400 bg-white/5 backdrop-blur"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-lime-400 animate-pulse" />
            Now serving in Bangalore
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-display font-black text-5xl sm:text-6xl lg:text-7xl xl:text-8xl tracking-tighter mt-6 leading-[0.95]"
          >
            Hungry? <br />
            <span className="text-lime-400">FUDK</span> delivers in <span className="underline decoration-lime-400 decoration-[6px] underline-offset-[10px]">28 min.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mt-6 max-w-2xl text-base md:text-lg text-white/80 leading-relaxed"
          >
            From sizzling kebabs in Basavanagudi to fresh filter coffee at sunrise — discover 8,400+ kitchens
            and get them at your door, fast.
          </motion.p>

          {/* Location bar */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-8 flex flex-col sm:flex-row gap-3 max-w-xl"
          >
            <div className="flex items-center gap-2 flex-1 bg-white text-black rounded-full px-5 py-3 shadow-lg">
              <MapPin size={18} className="text-[#84CC16]" />
              <input
                data-testid="hero-location-input"
                type="text"
                placeholder="Enter your delivery location"
                className="flex-1 outline-none text-sm placeholder-black/50 bg-transparent"
              />
            </div>
            <a
              href="/restaurants"
              data-testid="hero-find-food-btn"
              className="inline-flex items-center justify-center bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold text-sm px-7 py-3 rounded-full transition-colors"
            >
              Find Food
            </a>
          </motion.div>

          {/* App badges */}
          <motion.div
            id="download"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.45 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <span className="text-xs uppercase tracking-[0.25em] text-white/60 mr-1">Download the App</span>
            <a
              href="#"
              data-testid="hero-appstore-btn"
              className="group flex items-center gap-3 bg-black/70 border border-white/15 hover:border-lime-400 backdrop-blur px-5 py-3 rounded-2xl transition-colors"
            >
              <FaApple size={26} className="text-white" />
              <div className="leading-tight text-left">
                <div className="text-[10px] uppercase tracking-widest text-white/60">Download on the</div>
                <div className="text-base font-semibold text-white">App Store</div>
              </div>
            </a>
            <a
              href="#"
              data-testid="hero-playstore-btn"
              className="group flex items-center gap-3 bg-black/70 border border-white/15 hover:border-lime-400 backdrop-blur px-5 py-3 rounded-2xl transition-colors"
            >
              <FaGooglePlay size={22} className="text-[#84CC16]" />
              <div className="leading-tight text-left">
                <div className="text-[10px] uppercase tracking-widest text-white/60">Get it on</div>
                <div className="text-base font-semibold text-white">Google Play</div>
              </div>
            </a>
          </motion.div>
        </div>

        {/* Bottom marquee */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 1 }}
          className="mt-12 flex items-center justify-between gap-6 text-white/60"
        >
          <div className="hidden md:flex items-center gap-6 text-xs uppercase tracking-[0.3em]">
            <span>Biryani</span>
            <span>Pizza</span>
            <span>Tandoor</span>
            <span>Filter Coffee</span>
            <span>Chaat</span>
            <span>Continental</span>
          </div>
          <a href="#about" data-testid="hero-scroll-cue" className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] hover:text-lime-400">
            Scroll <ArrowDown size={14} />
          </a>
        </motion.div>
      </div>
    </section>
  );
}
